/**
 * Calendar arithmetic on plain `YYYY-MM-DD` strings.
 *
 * Never `Date` objects. A billing date, a period boundary and a chart bucket
 * are all calendar facts, and the moment one becomes a `Date` it acquires a
 * time and a zone it never had — which is how a contract billed on the 1st
 * bills on the 31st for a tenant behind UTC, invisibly, in any test that runs
 * in a single time zone.
 *
 * Its own module because very different callers need it: billing, which
 * decides when an invoice exists; the list engine's stat cards, which decide
 * what "quarter to date" means; and — since v3 §5 — the date picker, which
 * paints the range a preset resolves to. Any of them importing another would
 * be the wrong way round, and the picker is why this is in `shared` rather
 * than under `server/src/services`: it is pure string arithmetic with no
 * reason ever to have been server-only, and a second copy on the client
 * would be a second answer to "what does last month mean".
 */
import { ApiError } from './errors.js';
export function parseDay(day) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
    if (!match)
        throw new ApiError('validation_failed', `Not a date: ${day}`);
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}
const pad = (value) => String(value).padStart(2, '0');
export const formatDay = (parts) => `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
/** The last day of a month, so "the 31st" of a 30-day month lands on the 30th. */
export function daysInMonth(year, month) {
    return [31, isLeap(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
}
const isLeap = (year) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
export function addMonths(day, months) {
    const parts = parseDay(day);
    const zeroBased = parts.year * 12 + (parts.month - 1) + months;
    const year = Math.floor(zeroBased / 12);
    const month = (zeroBased % 12) + 1;
    return formatDay({ year, month, day: Math.min(parts.day, daysInMonth(year, month)) });
}
export function addDays(day, days) {
    const parts = parseDay(day);
    // Midday UTC, so a daylight-saving shift in either direction cannot move the
    // date across a boundary on the way back out.
    const at = Date.UTC(parts.year, parts.month - 1, parts.day, 12);
    const moved = new Date(at + days * 86_400_000);
    return formatDay({
        year: moved.getUTCFullYear(), month: moved.getUTCMonth() + 1, day: moved.getUTCDate(),
    });
}
export const compareDays = (a, b) => a < b ? -1 : a > b ? 1 : 0;
/** The first and last day of the month a date falls in. */
export function monthOf(day) {
    const { year, month } = parseDay(day);
    return {
        start: formatDay({ year, month, day: 1 }),
        end: formatDay({ year, month, day: daysInMonth(year, month) }),
    };
}
/** The first day of the day/week/month/quarter/year `day` falls in. */
export function startOf(day, unit) {
    const parts = parseDay(day);
    switch (unit) {
        case 'day': return day;
        case 'week': {
            // Monday, which is what a week means to everyone who runs crews.
            const at = Date.UTC(parts.year, parts.month - 1, parts.day, 12);
            const weekday = (new Date(at).getUTCDay() + 6) % 7;
            return addDays(day, -weekday);
        }
        case 'month': return formatDay({ ...parts, day: 1 });
        case 'quarter':
            return formatDay({ year: parts.year, month: Math.floor((parts.month - 1) / 3) * 3 + 1, day: 1 });
        case 'year': return formatDay({ year: parts.year, month: 1, day: 1 });
    }
}
/** One bucket later — what walking a series does. */
export function addBucket(day, unit, count) {
    switch (unit) {
        case 'day': return addDays(day, count);
        case 'week': return addDays(day, count * 7);
        case 'month': return addMonths(day, count);
        case 'quarter': return addMonths(day, count * 3);
        case 'year': return addMonths(day, count * 12);
    }
}
/** Every bucket start from `from` to `to`, inclusive — including the empty ones. */
export function bucketsBetween(from, to, unit) {
    const buckets = [];
    let cursor = startOf(from, unit);
    // A chart with a gap where a month had no work is a chart that lies about
    // the shape of the year, so empty buckets are generated rather than skipped.
    while (compareDays(cursor, to) <= 0 && buckets.length < 400) {
        buckets.push(cursor);
        cursor = addBucket(cursor, unit, 1);
    }
    return buckets;
}
//# sourceMappingURL=calendar.js.map